import type { ILogger, LogContext, LogPayload } from "@smeetz/logger-common";
import { Order } from "@smeetz/orders";
import type { OrderOperationContext } from "@smeetz/orders";
import { DiscountPackage } from "@smeetz/discount-common";
import { initOffersLogger } from "@smeetz/offers-common";
import { initOrderStoreLogger } from "@smeetz/order-store";
import AppModule from "@/store/modules/app";
import POSConfigModule from "@/store/modules/pos-config";
import { LogLevel, LogService } from "@/services/logging";
import { generateUuidv4 } from "@/utils/helpers/app";
import {
  isBusinessDeclineError,
  orderErrorCauseLabel,
  redactDeclineError,
} from "@/utils/vue-helpers/order-action-errors.helper";

/**
 * Whether verbose logging is enabled on this device: debug mode toggled on,
 * one of the pilot groups, or a non-production build. Shared by the console
 * gate (setupConsoleLog) and the package debug lines below, so a device that
 * keeps its console output also keeps its package step traces.
 */
export function isVerboseLoggingEnabled(): boolean {
  return (
    AppModule.debugMode ||
    POSConfigModule.groupId === 1 ||
    POSConfigModule.groupId === 19066 ||
    POSConfigModule.isLocarno ||
    import.meta.env.VITE_ENV === "test" ||
    import.meta.env.VITE_ENV === "dev"
  );
}

/**
 * Fresh per-operation context for one user action (a tap: add lines, apply a
 * discount, pay…). Passed into the order-store actions, the requestId is
 * bound onto the packages' per-operation loggers, so one requestId search in
 * the log backend returns every package line of that single action. Mint one
 * per user action — never share across actions and never store on a
 * singleton.
 */
export function newOperationContext(): OrderOperationContext {
  return { requestId: generateUuidv4() };
}

/**
 * ILogger adapter over the POS logging pipeline, installed into the logger
 * registries of the @smeetz/* packages so their internal logs reach the log
 * backend through the same Dexie batch + sync pipeline as the app's own logs.
 * The app code itself keeps logging through LogService directly — this
 * adapter only serves package-emitted lines.
 */
class PackageLogger implements ILogger {
  constructor(private readonly context: LogContext = {}) {}

  debug(payload: LogPayload): void {
    // Step-tracing lines only ship where verbose logging is on.
    if (!isVerboseLoggingEnabled()) {
      return;
    }
    this.write(LogLevel.Info, payload);
  }

  info(payload: LogPayload): void {
    this.write(LogLevel.Info, payload);
  }

  warn(payload: LogPayload): void {
    this.write(LogLevel.Warning, payload);
  }

  error(payload: LogPayload): void {
    this.write(LogLevel.Error, payload);
  }

  /** Immutable child: merges context into a new logger, never mutates this one. */
  child(context: LogContext): ILogger {
    return new PackageLogger({ ...this.context, ...context });
  }

  /**
   * Renders the shared `[package][fn] message` prefix and forwards the
   * structured fields as LogService data, which stringifies them into the
   * log line's `params` (expanding real Error objects on the way). The
   * requestId travels in the same data object and LogService hoists it to a
   * top-level field on the log line, so the backend filters every line of
   * one user action on a single indexed field.
   * App name, env and version are NOT re-added here — LogService.meta()
   * already stamps them on every line.
   */
  private write(logLevel: LogLevel, payload: LogPayload): void {
    const pkg = this.context.package ?? "unknown-package";
    const fn = payload.fn ?? this.context.fn ?? "unknown";
    // The packages journal failures under generic messages ("order action
    // failed") with the cause buried in the error chain. The root-cause label
    // (SESSION_EXPIRED, a decline label…) is appended to the message so the
    // log backend can query and group package lines per cause.
    const cause =
      payload.err !== undefined ? orderErrorCauseLabel(payload.err) : undefined;
    const message = `[${pkg}][${fn}] ${payload.message}${
      cause ? `: ${cause}` : ""
    }`;
    // The packages journal every action failure at error level, business
    // declines (rejected promo/voucher codes) included. Declines are expected
    // selling outcomes, so their lines ship as WARNING — ERROR alerts on
    // package lines then only fire for defects.
    let level = logLevel;
    if (
      level === LogLevel.Error &&
      (isBusinessDeclineError(payload.err) ||
        isBusinessDeclineError(payload.meta))
    ) {
      level = LogLevel.Warning;
    }
    const data: Record<string, unknown> = {};
    if (this.context.requestId !== undefined) {
      data.requestId = this.context.requestId;
    }
    // Package payloads carry raw errors; a declined code/voucher embeds the
    // still-spendable code value in its messages and `code` fields, so those
    // chains are redacted here — the one funnel every package line crosses.
    if (payload.meta !== undefined) {
      data.meta = redactDeclineError(payload.meta);
    }
    if (payload.err !== undefined) {
      data.err = redactDeclineError(payload.err);
    }
    LogService.info(message, data, level);
  }
}

/**
 * Installs one shared PackageLogger into every loggable @smeetz/* package
 * registry. Call once at startup, before the package init functions run, so
 * even bootstrap failures inside the packages reach the log backend. When
 * this never runs, the packages fall back to their ConsoleLogger default.
 * Never throws — it runs synchronously inside app setup, and a throw would
 * abort the whole bootstrap.
 */
export function initializePackageLoggers(): void {
  try {
    const logger = new PackageLogger();
    Order.initLogger(logger);
    DiscountPackage.initLogger(logger);
    initOffersLogger(logger);
    initOrderStoreLogger(logger);
  } catch (error) {
    // Swallowed on purpose (bootstrap must survive); console.error keeps dev
    // visibility while LogService ships it.
    console.error("Package logger initialization failed:", error);
    LogService.info(
      "Package logger initialization failed",
      { error },
      LogLevel.Error
    );
  }
}
